import {
	PropsWithChildren,
	ReactElement,
	useState
} from "react";
import classNames from "classnames";
import { newGuid } from "client_legacy/ndaw/guid";
import { FormLabel } from "../FormLabel/FormLabel";
import { UniqueInputIdContext } from "../../Contexts/UniqueInputIdContext";
import styles from "./FormItem.module.less";
import { FormattedTextString } from "client/Misc/commonTypes";
import { resolveFormattedTextString } from "client/Misc/utils";
import { FieldsetLegend } from "../FormLabel/FieldsetLegend";

function useHtmlFor(): string {
	const [id] = useState<string>(() => newGuid());
	return id;
};

useHtmlFor.displayName = "useHtmlFor";

export type FormItemProps = {
	label?: string | FormattedTextString;
	isRequired?: boolean;
	hasNoMargin?: boolean;
	height?: "auto" | "stretch";
	isLabelEllipsed?: boolean;
	isForGroup?: boolean;
};

export const FormItem = (props: PropsWithChildren<FormItemProps>): ReactElement => {
	const {
		isForGroup = false,
		...restOfTheProps
	} = props;

	if (isForGroup) {
		return (
			<InputGroupFormItem
				{...restOfTheProps}
			/>
		);
	}

	return (
		<SingleInputFormItem
			{...restOfTheProps}
		/>
	);
};

FormItem.displayName = "FormItem";

const FormItemDescription = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<p className={styles.description}>
			{children}
		</p>
	);
};

FormItemDescription.displayName = "FormItem.Description";

FormItem.Description = FormItemDescription;

const SingleInputFormItem = (props: PropsWithChildren<Omit<FormItemProps, "isForGroup">>): ReactElement => {
	const {
		isRequired,
		hasNoMargin = false,
		height = "auto",
		isLabelEllipsed = false,
		children
	} = props;

	const id: string = useHtmlFor();
	const label: string | undefined= resolveFormattedTextString(props.label);

	return (
		<div className={classNames(
			styles.root,
			{
				[styles.root_noMargin]: hasNoMargin,
				[styles.root_stretchHeight]: height === "stretch"
			}
		)}>
			<UniqueInputIdContext.Provider value={{ id }}>
				{label && (
					<FormLabel
						isRequired={isRequired}
						isEllipsed={isLabelEllipsed}
						tooltip={label}
					>
						{label}
					</FormLabel>
				)}

				<div
					className={styles.controlsColumn}
				>
					{ children }
				</div>
			</UniqueInputIdContext.Provider>
		</div>
	);
};
SingleInputFormItem.displayName = "SingleInputFormItem";

const InputGroupFormItem = (props: PropsWithChildren<Omit<FormItemProps, "isForGroup">>): ReactElement => {
	const {
		isRequired,
		hasNoMargin = false,
		height = "auto",
		isLabelEllipsed = false,
		children
	} = props;

	const label: string | undefined= resolveFormattedTextString(props.label);

	return (
		<fieldset className={classNames(
			styles.root,
			{
				[styles.root_noMargin]: hasNoMargin,
				[styles.root_stretchHeight]: height === "stretch"
			}
		)}>
			{label && (
				<FieldsetLegend
					isRequired={isRequired}
					isEllipsed={isLabelEllipsed}
					tooltip={label}
				>
					{label}
				</FieldsetLegend>
			)}
			<div className={styles.controlsColumn}>
				{children}
			</div>
		</fieldset>
	);
};
InputGroupFormItem.displayName = "InputGroupFormItem";
